import { MCP_APPROVAL_TARGETS_QUERY_KEY } from "../constants/connector-approval";
import { INTEGRATION_QUERY_KEY } from "../constants/integration";
import {
  EConnectorType,
  EIntegrationProvider,
  type IEnabledIntegrationProviderInfo,
} from "../types/integration";

// NOTE: Single source of truth for "which connectors can this provider be
// connected through?", consumed by every surface that opens SelectConnectorModal
// (the integrations page, the chat integration action, and the mobile upload
// picker — web + mobile).
//
// This used to be copy-pasted at five call sites, which is how Pipedream
// shipped reachable from only two of them: the server had already flipped
// `showConnectorPicker` to "more than one connector available", so the three
// stale copies popped a modal listing fewer options than the server counted —
// in a Pipedream-without-Composio deployment, a modal with a single option.
//
// Order matters: it is the order the options render in. Keep it aligned with
// getDefaultConnectorType on the server (native first, then Composio, then
// Pipedream). Adding a fourth connector should mean editing THIS function only.
export const getAvailableConnectors = (
  info: IEnabledIntegrationProviderInfo | undefined,
): EConnectorType[] => {
  if (!info) return [];

  const connectors: EConnectorType[] = [];
  if (info.hasNodeshiftConnector) connectors.push(EConnectorType.INTERNAL);
  if (info.hasComposioConnector) connectors.push(EConnectorType.COMPOSIO);
  if (info.hasPipedreamConnector) connectors.push(EConnectorType.PIPEDREAM);
  return connectors;
};

/**
 * NOTE: NSAI-5354 — every query a connection being added or removed invalidates.
 *
 * `INTEGRATION_QUERY_KEY` alone was not enough for MCP. The approval panel reads
 * a SEPARATE query (`MCP_APPROVAL_TARGETS_QUERY_KEY`) whose rows are discovered
 * live from each connected server, and it carries a 60s `staleTime` because every
 * fetch opens one handshake per server. So connecting a second MCP server left
 * the panel serving the cached single-server shape for up to a minute — the
 * member saw the one-server layout for the server they had ALREADY connected, and
 * only a manual page refresh showed both.
 *
 * Scoped to MCPS deliberately: this key is the one query in the app whose refetch
 * costs a network handshake per connected server, so invalidating it after an
 * unrelated provider's connect would spend that for nothing.
 *
 * Returned as a list rather than invalidated here because `QueryClient` comes
 * from `@tanstack/react-query`, which this package does not depend on — the five
 * call sites (web connect/disconnect, mobile connect ×2/disconnect) iterate it.
 */
export const getConnectionChangeQueryKeys = (
  provider: EIntegrationProvider | string | undefined,
): string[][] => [
  [INTEGRATION_QUERY_KEY],
  ...(provider === EIntegrationProvider.MCPS
    ? [[MCP_APPROVAL_TARGETS_QUERY_KEY]]
    : []),
];
